import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';

const features = [
  {
    title: 'Transport Scheduling',
    description: 'Plan and execute transport orders with precision — meeting deadlines efficiently every time.',
    icon: (
      <svg className="w-6 h-6 text-accent" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0v-7.5A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 11.25v7.5" />
      </svg>
    ),
  },
  {
    title: 'Fleet Monitoring',
    description: 'Real-time GPS tracking and live status updates across your entire fleet from one dashboard.',
    icon: (
      <svg className="w-6 h-6 text-accent" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M15 10.5a3 3 0 11-6 0 3 3 0 016 0z" />
        <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1115 0z" />
      </svg>
    ),
  },
  {
    title: 'Driver App Integration',
    description: 'Connected mobile app empowers drivers with job assignments, GPS routes, and easy reporting.',
    icon: (
      <svg className="w-6 h-6 text-accent" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 1.5H8.25A2.25 2.25 0 006 3.75v16.5a2.25 2.25 0 002.25 2.25h7.5A2.25 2.25 0 0018 20.25V3.75a2.25 2.25 0 00-2.25-2.25H13.5m-3 0V3h3V1.5m-3 0h3" />
      </svg>
    ),
  },
  {
    title: 'Asset Management',
    description: 'Track and manage all transport assets, vehicles, and equipment in one unified platform.',
    icon: (
      <svg className="w-6 h-6 text-accent" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 18.75a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m3 0h6m-9 0H3.375a1.125 1.125 0 01-1.125-1.125V14.25m17.25 4.5a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m3 0h1.125c.621 0 1.129-.504 1.09-1.124a17.902 17.902 0 00-3.213-9.193 2.056 2.056 0 00-1.58-.86H14.25M16.5 18.75h-2.25m0-11.177v-.958c0-.568-.422-1.048-.987-1.106a48.554 48.554 0 00-10.026 0 1.106 1.106 0 00-.987 1.106v7.635m12-6.677v6.677m0 4.5v-4.5m0 0h-12" />
      </svg>
    ),
  },
  {
    title: 'Compliance Management',
    description: 'Built-in compliance tools designed for Australian agricultural and logistics regulations.',
    icon: (
      <svg className="w-6 h-6 text-accent" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" />
      </svg>
    ),
  },
  {
    title: 'Reporting & Analytics',
    description: 'Detailed insights and performance reports to keep your operations running at peak efficiency.',
    icon: (
      <svg className="w-6 h-6 text-accent" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z" />
      </svg>
    ),
  },
];

const Logistics = () => {
  return (
    <>
      {/* Hero */}
      <section className="section-padding bg-white relative overflow-hidden">
        <div className="absolute inset-0 bg-mesh-grid pointer-events-none" />
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-border-subtle to-transparent" />

        <div className="section-container relative">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">

            {/* Left — text */}
            <motion.div
              initial={{ opacity: 0, x: -24 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              <span className="section-label">
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 18.75a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m3 0h6m-9 0H3.375a1.125 1.125 0 01-1.125-1.125V14.25m17.25 4.5a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m3 0h1.125c.621 0 1.129-.504 1.09-1.124a17.902 17.902 0 00-3.213-9.193 2.056 2.056 0 00-1.58-.86H14.25M16.5 18.75h-2.25m0-11.177v-.958c0-.568-.422-1.048-.987-1.106a48.554 48.554 0 00-10.026 0 1.106 1.106 0 00-.987 1.106v7.635m12-6.677v6.677m0 4.5v-4.5m0 0h-12" />
                </svg>
                Transport Management
              </span>
              <h1 className="text-5xl md:text-6xl font-bold text-primary mt-2 mb-6 leading-tight">
                Clutch<br />
                <span className="text-gradient-blue">Logistics</span>
              </h1>
              <p className="text-secondary text-lg leading-relaxed mb-8">
                A powerful, integrated solution combining a feature-rich logistics web application with the
                intuitive Clutch Driver App. Designed for exporters and large-scale agricultural operations —
                manage assets, schedule transports, and monitor fleets from a single platform.
              </p>
              <div className="flex flex-wrap gap-3">
                <Link to="/pricing" className="btn-primary">
                  See Pricing
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M13 7l5 5-5 5M6 12h12" />
                  </svg>
                </Link>
                <a
                  href="https://go.clutchit.com.au/auth/signup"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-ghost"
                >
                  Start Free Trial
                </a>
              </div>
            </motion.div>

            {/* Right — image */}
            <motion.div
              initial={{ opacity: 0, x: 24 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="relative"
            >
              <motion.div
                animate={{ y: [0, -8, 0] }}
                transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
              >
              <div className="overflow-hidden">
                <img
                  src="./images/clutch_logistics.png"
                  alt="Clutch Logistics Platform"
                  className="w-full h-auto object-cover"
                />
              </div>
              </motion.div>
              {/* Launch button */}
              <button
                onClick={() => window.open('https://go.clutchit.com.au/', '_blank')}
                className="absolute right-4 bottom-4 w-12 h-12 bg-accent text-white rounded-full flex items-center justify-center hover:bg-accent-hover hover:shadow-glow-sm transition-all duration-200 cursor-pointer"
                aria-label="Open Clutch Logistics"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M13 7l5 5-5 5M6 12h12" />
                </svg>
              </button>
              {/* Product label */}
              <div className="mt-4">
                <p className="text-primary font-semibold">Clutch Logistics</p>
                <p className="text-secondary text-sm">Platform / Mobile Application</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="section-padding bg-surface-1 relative overflow-hidden">
        <div className="absolute inset-0 bg-mesh-grid pointer-events-none" />
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-border-medium to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-border-medium to-transparent" />

        <div className="section-container relative">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-primary mb-4">
              Everything You Need to<br />
              <span className="text-gradient-blue">Run Your Fleet</span>
            </h2>
            <p className="text-secondary text-lg max-w-2xl mx-auto">
              Clutch Logistics brings together every tool your operation needs — in one connected platform.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {features.map(({ title, description, icon }, i) => (
              <motion.div
                key={title}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08, duration: 0.5 }}
                className="rounded-2xl border border-border-subtle bg-white p-6 shadow-card hover:border-accent hover:shadow-glow-sm transition-all duration-300 group"
              >
                <div className="w-12 h-12 rounded-xl bg-accent/10 flex items-center justify-center mb-4 group-hover:bg-accent/20 transition-colors duration-300">
                  {icon}
                </div>
                <h3 className="text-primary font-semibold mb-2">{title}</h3>
                <p className="text-secondary text-sm leading-relaxed">{description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Detail + screenshots */}
      <section className="section-padding bg-white relative overflow-hidden">
        <div className="absolute inset-0 bg-mesh-grid pointer-events-none" />

        <div className="section-container relative">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">

            {/* Screenshots */}
            <motion.div
              initial={{ opacity: 0, x: -24 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="grid grid-cols-3 gap-3"
            >
              {['/images/logo.png', '/images/Product-2.png', '/images/Product-3.png'].map((src, i) => (
                <div key={i} className="rounded-xl border border-border-subtle overflow-hidden shadow-card aspect-square bg-surface-1">
                  <img src={src} alt="" className="w-full h-full object-cover" />
                </div>
              ))}
            </motion.div>

            {/* Detail text + CTA */}
            <motion.div
              initial={{ opacity: 0, x: 24 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              <h2 className="text-3xl font-bold text-primary mb-4">
                Web Platform Meets<br />
                <span className="text-gradient-blue">Mobile Power</span>
              </h2>
              <p className="text-secondary leading-relaxed mb-4">
                The web application enables seamless planning, organisation, and execution of transport orders,
                ensuring you meet deadlines efficiently. Meanwhile, the Clutch Driver App empowers drivers with
                real-time job assignments, GPS-tracked routes, delivery status updates, and easy reporting — all
                from their smartphones.
              </p>
              <p className="text-secondary leading-relaxed mb-8">
                This synergy between the web platform and mobile app ensures smooth communication, timely deliveries,
                and enhanced coordination between dispatch teams and drivers.
              </p>
              <Link to="/pricing" className="btn-primary">
                View Pricing
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M13 7l5 5-5 5M6 12h12" />
                </svg>
              </Link>
            </motion.div>
          </div>
        </div>
      </section>
    </>
  );
};

export default Logistics;
